import React, { useState, useEffect, useCallback } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const SCORING_COMPONENT_TYPES = [
    { value: 'BOOL', label: 'True / False' },
    { value: 'INT', label: 'Whole Number' },
    { value: 'DECIMAL', label: 'Decimal Number' }
];

export default function ScoringComponentManagement() {
    const [events, setEvents] = useState([]);
    const [selectedEventID, setSelectedEventID] = useState('');
    const [competitions, setCompetitions] = useState([]);
    const [selectedCompetitionID, setSelectedCompetitionID] = useState('');
    const [scoringComponents, setScoringComponents] = useState([]);
    const [newScoringComponent, setNewScoringComponent] = useState({
        ScoringComponentID: null,
        ScoringComponentName: '',
        Points: '',
        ScoringComponentType: 'INT' // Default type
    });
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getAllEvents`);
                const data = await response.json();
                const enabledEvents = Array.isArray(data) ? data.filter(event => event.Enabled) : [];
                setEvents(enabledEvents);
                
                // Auto-select first event
                if (enabledEvents.length > 0) {
                    setSelectedEventID(enabledEvents[0].EventID);
                }
            } catch (error) {
                console.error('Error fetching events:', error);
            }
        };
        fetchEvents();
    }, []);

    useEffect(() => {
        if (!selectedEventID) return;
        const fetchCompetitions = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getCompetitions?EventID=${selectedEventID}`);
                const data = await response.json();
                setCompetitions(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching competitions:', error);
            }
        };
        fetchCompetitions();
    }, [selectedEventID]);

    useEffect(() => {
        if (selectedCompetitionID) {
            fetchScoringComponents(selectedCompetitionID);
        }
    }, [selectedCompetitionID]);

    const fetchScoringComponents = async (CompetitionID) => {
        try {
            const response = await fetch(`${API_BASE_URL}/getScoringComponents?CompetitionID=${CompetitionID}`);
            const data = await response.json();
            setScoringComponents(Array.isArray(data) ? data : []); // Removed filter for 'Base Score'
        } catch (error) {
            console.error('Error fetching scoring components:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewScoringComponent(prev => ({ ...prev, [name]: value }));
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setNewScoringComponent({
            ScoringComponentID: null,
            ScoringComponentName: '',
            ScoringComponentType: 'INT',
            Points: ''
        });
    };

    const isDuplicateName = (name, id = null) => {
        const normalized = name.trim().toLowerCase();
        return scoringComponents.some(sc => {
            if (id && sc.ScoringComponentID === id) return false;
            return (sc.ScoringComponentName || '').trim().toLowerCase() === normalized;
        });
    };

    const handleAddOrUpdateScoringComponent = async () => {
        if (!newScoringComponent.ScoringComponentName.trim()) {
            alert('Scoring component name is required!');
            return;
        }
        if (isDuplicateName(newScoringComponent.ScoringComponentName, newScoringComponent.ScoringComponentID)) {
            alert('A scoring component with this name already exists in this competition.');
            return;
        }

        const method = newScoringComponent.ScoringComponentID ? 'PUT' : 'POST';
        const endpoint = newScoringComponent.ScoringComponentID ? `${API_BASE_URL}/updateScoringComponent` : `${API_BASE_URL}/createScoringComponent`;

        const response = await fetch(endpoint, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...newScoringComponent, CompetitionID: selectedCompetitionID })
        });

        if (response.ok) {
            fetchScoringComponents(selectedCompetitionID);
            closeModal();
        } else {
            alert('Error adding/updating scoring component.');
        }
    };

    const handleDeleteScoringComponent = async (ScoringComponentID) => {
        const confirmDelete = window.confirm('!!!Warning!!! This will delete any associated score entries and cannot be undone! Are you sure you want to delete this scoring component?');
        if (!confirmDelete) return;

        try {
            const response = await fetch(`${API_BASE_URL}/deleteScoringComponent?ScoringComponentID=${ScoringComponentID}`, { method: 'DELETE' });
            if (response.ok) {
                fetchScoringComponents(selectedCompetitionID);
            } else {
                alert('Error deleting scoring component.');
            }
        } catch (error) {
            console.error('Error deleting scoring component:', error);
        }
    };

    const handleEditScoringComponent = (component) => {
        setNewScoringComponent(component);
        openModal();
    };

    const handleCancelEdit = () => {
        setNewScoringComponent({
            ScoringComponentID: null,
            ScoringComponentName: '',
            ScoringComponentType: 'INT', // Reset to default type
            Points: ''
        });
    };

    return (
        <div className="p-1">
            <div className="flex mb-1">
                <label className="form-control-inline">
                    <select className="form-select" value={selectedEventID} onChange={(e) => setSelectedEventID(e.target.value)}>
                        <option value="">Select Event</option>
                        {events.map(event => (
                            <option key={event.EventID} value={event.EventID}>{event.EventName}</option>
                        ))}
                    </select>
                </label>

                <label className="form-control-inline">
                    <select className="form-select" value={selectedCompetitionID} onChange={(e) => setSelectedCompetitionID(e.target.value)} disabled={!selectedEventID}>
                        <option value="">Select Competition</option>
                        {competitions.map(comp => (
                            <option key={comp.CompetitionID} value={comp.CompetitionID}>{comp.CompetitionName}</option>
                        ))}
                    </select>
                </label>
            </div>

            {selectedCompetitionID && (
                <button className="btn btn-primary mb-1" onClick={openModal}>Add Scoring Component</button>
            )}

            {scoringComponents.length > 0 ? (
                <div className="data-table-wrapper">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Scoring Component Name</th>
                                <th>Type</th>
                                <th>Points</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {scoringComponents.map(comp => (
                                <tr key={comp.ScoringComponentID}>
                                    <td title={
                                        comp.ScoringComponentName === 'Minutes' || comp.ScoringComponentName === 'Seconds' 
                                        ? 'Automatically Created Time Scoring Component' 
                                        : comp.ScoringComponentName === 'Disqualified' 
                                        ? 'Automatically Added Disqualified Component results in a failing score' 
                                        : ''
                                    }>
                                        {comp.ScoringComponentName}
                                    </td>
                                    <td>{SCORING_COMPONENT_TYPES.find(type => type.value === comp.ScoringComponentType)?.label}</td>
                                    <td>{comp.ScoringComponentName === 'Disqualified' ? '∞' : (comp.Points > 0 ? `+${comp.Points}` : comp.Points)}</td>
                                    <td>
                                        {comp.ScoringComponentName !== 'Minutes' && comp.ScoringComponentName !== 'Seconds' && comp.ScoringComponentName !== 'Disqualified' && (
                                            <div className="flex gap-1">
                                                <button className="btn btn-primary" onClick={() => handleEditScoringComponent(comp)}>Edit</button>
                                                <button className="btn btn-danger" onClick={() => handleDeleteScoringComponent(comp.ScoringComponentID)}>Delete</button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p>No Scoring Components found for this competition.</p>
            )}

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>{newScoringComponent.ScoringComponentID ? 'Edit Scoring Component' : 'Add Scoring Component'}</h3>
                        <p style={{ color: '#ff6b6b', marginBottom: '1rem' }}>Warning: It is dangerous to change scoring components after competition start.</p>
                        <input
                            className="form-control"
                            type="text"
                            name="ScoringComponentName"
                            placeholder="Scoring Component Name"
                            value={newScoringComponent.ScoringComponentName}
                            onChange={handleInputChange}
                        />
                        <select
                            className="form-select"
                            name="ScoringComponentType"
                            value={newScoringComponent.ScoringComponentType}
                            onChange={handleInputChange}
                            title="Select the type of scoring component: True / False, Whole Number, or Decimal Number"
                        >
                            {SCORING_COMPONENT_TYPES.map(type => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                        </select>
                        <input
                            className="form-control"
                            type="number"
                            name="Points"
                            placeholder="Points"
                            value={newScoringComponent.Points}
                            onChange={handleInputChange}
                        />
                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={closeModal}>
                                Cancel
                            </button>
                            <button className="btn btn-primary" onClick={handleAddOrUpdateScoringComponent}>
                                {newScoringComponent.ScoringComponentID ? 'Update Scoring Component' : 'Add Scoring Component'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}